// Command Center page — the three-column v2 rebuild.
//
// Route: /thread/:id
//
// Layout (desktop):
//   cc-topbar:     Back • brand/channel/order tags • thread-health dot • bucket counter • prev/next (J/K)
//   cc-grid:
//     cust-panel   left 260px sticky  — customer identity + lifetime stats + order history + notes
//     work-col     center flex        — AI-summary subject headline, thread (iMessage bubbles)
//     actions-panel right 300px sticky — Haiku's 3 ranked next moves + composer drawer
//
// On resolve/snooze/spam or on firing a proposed action, we auto-advance to
// the next id in the bucket (if any). If this is the last thread in the
// bucket we navigate back to Standup. Bucket context comes from router
// location state (Standup's DecideCard sets `state.ids[]`).
//
// Shortcuts:
//   J / K           prev / next (handled by CommandCenterTopBar)
//   1 / 2 / 3       fire proposed action 1/2/3 (handled by ActionsPanel)
//   C               open Composer drawer for a custom reply
//   O / A / R / !   status transitions (existing useStatusShortcuts)
//   Z               open SnoozeMenu
//
// Sticky topbar lives outside cc-grid so it spans full width. cc-grid's
// panels are sticky relative to the viewport, not to cc-topbar.

import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { TopNav, type BrandFilter } from '@/components/TopNav';
import { useConversation } from '@/lib/hooks/useConversation';
import { useStatusShortcuts } from '@/lib/hooks/useStatusShortcuts';
import { useBucketNav } from '@/lib/hooks/useBucketNav';
import { useThreadTones } from '@/lib/hooks/useThreadTones';
import { useOrderContext } from '@/lib/hooks/useOrderContext';
import { computeThreadHealth } from '@/lib/threadHealth';
import { CommandCenterTopBar } from '@/components/center/CommandCenterTopBar';
import { CustomerPanel } from '@/components/center/CustomerPanel';
import { IntentBadgeRow } from '@/components/center/IntentBadgeRow';
import { OrderProblemBanner } from '@/components/center/OrderProblemBanner';
import { DuplicateThreadBanner } from '@/components/center/DuplicateThreadBanner';
import { ThreadView } from '@/components/center/ThreadView';
import type { Attachment } from '@/components/center/AttachmentGrid';
import { BriefingBlock } from '@/components/center/BriefingBlock';
import { ActionsPanel } from '@/components/center/ActionsPanel';
import { TeamPingPanel, type TeamPingPanelHandle } from '@/components/center/TeamPingPanel';
import { Composer, type ComposerHandle } from '@/components/center/Composer';
import { ProofTrackerPanel } from '@/components/center/ProofTrackerPanel';
import { useProofs, useMarkProof } from '@/lib/hooks/useAttachments';
import { StatusPill } from '@/components/center/StatusPill';
import { SnoozeMenu } from '@/components/center/SnoozeMenu';
import { LabelEditor } from '@/components/center/LabelEditor';
import { ShortcutHelp } from '@/components/center/ShortcutHelp';
import { cleanSubject, prettifyEmailLocal } from '@/lib/emailClean';
import { useEffect, useMemo } from 'react';
import type { MessageTone } from '@/components/center/ToneChip';
import { useEnsureSummaries } from '@/lib/hooks/useEnsureSummaries';
import { useEnsureIntents } from '@/lib/hooks/useEnsureIntents';

export function CenterPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useAuth();
  // Brand filter persists locally on Center; changing it sends the user back
  // to Standup (there's no thread-list here to re-filter).
  const [brand, setBrand] = useState<BrandFilter>('all');

  const { data, isLoading, error } = useConversation(id);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Imperative handle to the sticky Composer — used by ActionsPanel's
  // "Or write from scratch" button and the `C` shortcut to bring focus
  // to the reply box without opening a second composer drawer.
  const composerRef = useRef<ComposerHandle | null>(null);
  const focusComposer = useCallback(() => {
    composerRef.current?.focus();
  }, []);
  const seedComposer = useCallback((text: string) => {
    composerRef.current?.setBody(text);
  }, []);

  // Polish #10 — TeamPingPanel now lives always-visible in the right rail.
  // The `S` shortcut focuses its textarea instead of popping a drawer.
  const teamPingRef = useRef<TeamPingPanelHandle | null>(null);
  const focusTeamPing = useCallback(() => {
    teamPingRef.current?.focus();
  }, []);

  // `?` toggles the keyboard shortcut help modal. Ignored while typing.
  // `S` focuses the always-visible team ping panel (Polish #10).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t?.isContentEditable ?? false)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setHelpOpen((v) => !v);
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        focusTeamPing();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusTeamPing]);

  // Bucket context from Standup (router state).
  const bucketNav = useBucketNav(id);

  // Tone classifier for inbound bubbles — fires once per thread load.
  const tonesMutation = useThreadTones();
  const [tones, setTones] = useState<Record<string, MessageTone>>({});
  const inboundIds = useMemo(
    () => (data?.messages ?? []).filter((m) => m.direction === 'inbound').map((m) => m.id),
    [data?.messages],
  );
  useEffect(() => {
    if (inboundIds.length === 0) return;
    tonesMutation.mutate(
      { messageIds: inboundIds },
      {
        onSuccess: (res) => setTones(res.tones),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, inboundIds.length]);

  // Status keyboard shortcuts — disabled while a menu is open.
  useStatusShortcuts({
    conversationId: id,
    enabled: !!id,
    onSnoozeRequest: () => setSnoozeOpen(true),
  });

  // Auto-advance on completion. Called by ActionsPanel after firing an
  // action, and by the SnoozeMenu/StatusPill via the same path (status
  // transitions invalidate the conversation, we let React Query refetch
  // but then we want to jump forward so Kimi stays in flow).
  const advance = useCallback(() => {
    if (bucketNav.nextId) {
      navigate(`/thread/${bucketNav.nextId}`, {
        state: { bucket: bucketNav.bucket, ids: bucketNav.remainingIds, label: bucketNav.bucketLabel },
        replace: true,
      });
    } else {
      navigate('/');
    }
  }, [bucketNav, navigate]);

  // Auto-advance when conversation transitions out of an actionable state.
  // Watch conv.status: when it changes to resolved / spam / snoozed (any of
  // which means Kimi just acted on it), jump to nextId in the bucket — same
  // as the explicit advance() callback used by ActionsPanel.
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = data?.conversation.status ?? null;
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (!status || !prev || status === prev) return;
    const terminal = status === 'resolved' || status === 'spam' || status === 'snoozed';
    const wasActionable = prev === 'open' || prev === 'awaiting';
    if (terminal && wasActionable) {
      // Defer one tick so any pending mutation onSuccess invalidations land
      // before we navigate away.
      setTimeout(() => advance(), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.conversation.status]);

  // Order context — fetched here so CustomerPanel + work-head can both read
  // the same cache entry without fighting. The hook itself gates on
  // conversationId; brand/email gating happens downstream when reading the
  // result (HK has no Shopify store, so the lookup will simply return empty).
  const orderContext = useOrderContext(id);

  // Kick off a summary generation for the currently open thread so the
  // work-head shows a real one-line headline instead of the raw subject
  // ("Contact form submission for Rachel Hovey"). The useConversation query
  // key will get bumped by the mutation onSuccess in the backend route, so
  // once the summary lands conv.ai_summary will populate on next refetch.
  useEnsureSummaries(id ? [id] : []);

  const conv = data?.conversation;

  // Polish #18 — group attachments by message id so ThreadView can render
  // paperclip chips + image thumbs under each bubble. Backend returns the
  // attachments embedded on each message (`m.attachments`) using `mime_type`;
  // AttachmentGrid expects `content_type`, so we re-shape here. We also carry
  // the attachment `id` through so outbound bubbles can wire the "Mark as
  // proof" button.
  const attachmentsByMessageId = useMemo<Record<string, Attachment[]>>(() => {
    const out: Record<string, Attachment[]> = {};
    for (const m of data?.messages ?? []) {
      const atts = m.attachments;
      if (!atts || atts.length === 0) continue;
      out[m.id] = atts
        .filter((a) => a.url)
        .map((a) => ({
          id: a.id,
          filename: a.filename,
          content_type: a.mime_type ?? null,
          url: a.url as string,
          size_bytes: a.size_bytes,
        }));
    }
    return out;
  }, [data?.messages]);

  // Polish #18 — proof tracker wiring. useProofs feeds the right-rail panel
  // and also tells us which attachment ids already have a proof row so the
  // "Mark as proof" button on outbound attachments can flip to a "✓ Proof"
  // badge. useMarkProof is the POST /proofs mutation we hand to AttachmentGrid.
  const proofsQ = useProofs(id);
  const markProof = useMarkProof(id);
  const proofAttachmentIds = useMemo<Set<string>>(() => {
    const out = new Set<string>();
    for (const p of proofsQ.data?.proofs ?? []) {
      if (p.attachment?.id) out.add(p.attachment.id);
    }
    return out;
  }, [proofsQ.data]);
  const handleMarkProof = useCallback(
    (attachmentId: string) => {
      markProof.mutate({ attachmentId });
    },
    [markProof],
  );

  // Resolve the customer's real display name. Priority chain:
  //   1. Shopify's displayName (firstName+lastName) when the customer exists
  //      in either MD or Brokerage. This wins because it's the most
  //      authoritative source — Shopify only has the name if the customer
  //      typed it into checkout.
  //   2. Latest inbound message's sender_name (Gmail From-header display name
  //      like "Rachel Hovey <rachel.hovey@bhhsamb.com>" → "Rachel Hovey").
  //      Skip values that are just the email address again.
  //   3. Prettify the email local part: "sarah.fellah@provaus.com" →
  //      "Sarah Fellah". Strips digits, splits on . _ - +, title-cases each
  //      word. Better than a raw email in 38px Larken.
  //   4. null only if we have nothing at all.
  const customerName = useMemo(() => {
    const cust = orderContext.data?.customer;
    // Prefer first+last composed. Fall back to displayName. Never use an @.
    const firstLast = cust ? [cust.firstName, cust.lastName].filter(Boolean).join(' ').trim() : '';
    if (firstLast && !firstLast.includes('@') && firstLast.length >= 2) {
      return firstLast;
    }
    const shopifyName = cust?.displayName?.trim();
    if (shopifyName && !shopifyName.includes('@') && shopifyName.length >= 2) {
      return shopifyName;
    }
    const messages = data?.messages ?? [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m || m.direction !== 'inbound') continue;
      const raw = m.sender_name?.trim();
      if (!raw) continue;
      if (raw.includes('@')) continue;
      if (raw.length < 2) continue;
      return raw;
    }
    return prettifyEmailLocal(conv?.customer_email ?? null);
  }, [
    orderContext.data?.customer?.firstName,
    orderContext.data?.customer?.lastName,
    orderContext.data?.customer?.displayName,
    data?.messages,
    conv?.customer_email,
  ]);

  // Build initials from the resolved name. "Rachel Hovey" → "RH",
  // "sarah@hendricksrealty.com" → "S". Max 2 chars.
  const customerInitials = useMemo(() => {
    if (!customerName) return 'C';
    const parts = customerName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
    }
    return parts[0]!.charAt(0).toUpperCase();
  }, [customerName]);

  const health = conv
    ? computeThreadHealth({
        lastMessageAt: conv.last_message_at,
        lastMessageDirection: conv.last_message_direction,
      })
    : null;

  // Derive the "primary order" tag — most recent order's number, if any.
  // Used in the topbar next to brand/channel.
  const primaryOrderTag = useMemo(() => {
    const orders = orderContext.data?.orders ?? [];
    return orders[0]?.name ?? null;
  }, [orderContext.data]);

  // Polish #10 — source the order hint for TeamPingPanel from the intent
  // classifier (regex on latest inbound body). Falls back to the Shopify
  // primaryOrderTag when the customer didn't type an order number but we
  // already matched them to an order record. That way the "Order" chip
  // has something meaningful to insert in almost every real thread.
  const intentsQ = useEnsureIntents(id ? [id] : []);
  const orderHint = useMemo(() => {
    const fromClassifier = id ? intentsQ.data?.intents[id]?.order_number_hint ?? null : null;
    return fromClassifier ?? primaryOrderTag;
  }, [id, intentsQ.data, primaryOrderTag]);

  // Human-facing channel label for the TeamPingPanel templates. Keep this
  // in one place so adding a new channel only means one map entry.
  const channelLabel = useMemo(() => {
    const ch = conv?.channel ?? '';
    const map: Record<string, string> = {
      gmail: 'Gmail',
      etsy_md: 'Etsy MD',
      etsy_hk: 'Etsy HK',
      amazon: 'Amazon',
      shopify: 'Shopify',
    };
    return map[ch] ?? (ch ? ch.charAt(0).toUpperCase() + ch.slice(1) : 'this channel');
  }, [conv?.channel]);

  // Polish #15 — tracking hint for the quick-reply "Send tracking link"
  // template. Walks the primary order's fulfillments newest-first and
  // returns the first usable tracking URL (preferred) or number. Returns
  // null when there's no shipment yet — the template leaves the literal
  // `{tracking}` placeholder in place so Kimi can see she needs to
  // paste one in.
  const trackingHint = useMemo<string | null>(() => {
    const orders = orderContext.data?.orders ?? [];
    const primary = orders[0];
    if (!primary) return null;
    const fulfillments = [...(primary.fulfillments ?? [])].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    for (const f of fulfillments) {
      for (const t of f.tracking ?? []) {
        if (t.url) return t.url;
        if (t.number) return t.number;
      }
    }
    return null;
  }, [orderContext.data]);

  // Context object for the SavedRepliesMenu autofill. Kept stable-identity
  // via useMemo so the menu's grouping memo doesn't recompute on every
  // Center re-render.
  const templateContext = useMemo(
    () => ({ name: customerName, order: orderHint, tracking: trackingHint }),
    [customerName, orderHint, trackingHint],
  );

  return (
    <>
      <TopNav
        view="center"
        onViewChange={(v) => {
          if (v === 'standup') navigate('/');
          // 'center' is the current view — no-op. 'pipe' not built yet.
        }}
        brand={brand}
        onBrandChange={(b) => {
          setBrand(b);
          // Brand filter is a Standup-level concept; bounce back so the
          // filter actually applies to a thread list.
          navigate('/');
        }}
        onSearchClick={() => navigate('/search')}
        onAnalyticsClick={() => navigate('/analytics')}
      />

      {isLoading ? (
        <div className="wrap" style={{ padding: '48px 0', color: 'hsl(var(--ink) / 0.6)' }}>
          Loading thread…
        </div>
      ) : error ? (
        <div className="wrap" style={{ padding: '48px 0', color: 'hsl(var(--ink) / 0.8)' }}>
          Couldn't load that thread: {(error as Error).message}
        </div>
      ) : !data || !conv ? (
        <div className="wrap" style={{ padding: '48px 0' }}>
          Thread not found.
        </div>
      ) : (
        <div className="wrap">
          {/* Command Center topbar — Back • brand • channel • order • health • STATUS PILL • bucket nav
              Polish PR (Phase 1 #2, issue #8): the StatusPill used to live on a
              separate secondary row beneath the topbar. It now rides inside the
              frozen topbar via the `statusSlot` so Kimi can see + change status
              while scrolling the thread, without the extra row of chrome. */}
          <CommandCenterTopBar
            brand={conv.brand}
            channel={conv.channel}
            orderTag={primaryOrderTag}
            health={health}
            bucketLabel={bucketNav.bucketLabel}
            position={bucketNav.position}
            prevId={bucketNav.prevId}
            nextId={bucketNav.nextId}
            bucketIds={bucketNav.remainingIds}
            bucketKey={bucketNav.bucket}
            statusSlot={
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <StatusPill
                  conversationId={conv.id}
                  status={conv.status}
                  snoozedUntil={conv.snoozed_until}
                  snoozeOpen={snoozeOpen}
                  onOpenSnooze={() => setSnoozeOpen(true)}
                  onCloseSnooze={() => setSnoozeOpen(false)}
                />
                <SnoozeMenu
                  conversationId={conv.id}
                  open={snoozeOpen}
                  onClose={() => setSnoozeOpen(false)}
                />
              </div>
            }
          />

          {/* Labels row — was previously paired with the StatusPill, now holds
              just the LabelEditor. Kept so existing labels stay visible and
              editable under the topbar. */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '0 0 18px',
              borderBottom: '1px solid var(--ink-06)',
              marginBottom: 18,
            }}
          >
            <LabelEditor conversationId={conv.id} />
          </div>

          {/* Three-column grid: customer • work • actions */}
          <div className="cc-grid">
            <CustomerPanel
              conversationId={conv.id}
              brand={conv.brand}
              customerEmail={conv.customer_email}
              displayName={customerName ?? conv.customer_email ?? 'Unknown customer'}
              initials={customerInitials}
            />

            <div className="work-col">
              {/* Work head — AI summary as headline, with thread health
                  + meta row beneath the title (matches v2 mockup). */}
              <div className="work-head">
                <div className="eyebrow">The thread</div>
                {/* Phase 9 follow-up — intent + extracted order# from the
                    classifier. Renders nothing for generic threads. */}
                <IntentBadgeRow conversationId={conv.id} />
                <h1 className="subject">
                  {(() => {
                    const s = (conv as { ai_summary?: string | null }).ai_summary;
                    const h = (conv as { ai_summary_highlight?: string | null }).ai_summary_highlight;
                    if (s && s.trim().length > 0) {
                      if (h && s.includes(h)) {
                        const i = s.indexOf(h);
                        return (
                          <>
                            {s.slice(0, i)}
                            <em>{h}</em>
                            {s.slice(i + h.length)}
                          </>
                        );
                      }
                      return s;
                    }
                    const cleanedSubject = cleanSubject(conv.subject);
                    return cleanedSubject.length > 0 ? cleanedSubject : '(no subject)';
                  })()}
                </h1>
                <ThreadStatusRow
                  health={health}
                  messageCount={data.messages.length}
                  firstMessageAt={data.messages[0]?.sent_at ?? null}
                  lastMessageAt={
                    data.messages[data.messages.length - 1]?.sent_at ?? conv.last_message_at
                  }
                />
              </div>

              {/* Polish #4 — duplicate-thread banner. Kimi will want to fold
                  two threads into one on sight, so we pin this above the
                  order-problem banner rather than burying it in the
                  customer rail. Renders nothing when this customer has no
                  other active threads. */}
              <DuplicateThreadBanner
                conversationId={conv.id}
                customerEmail={conv.customer_email}
              />

              {/* Polish #14 — order-problem banner. Surfaces refund, cancel,
                  late, stuck, or slow-transit signals on the primary order
                  so Kimi sees context BEFORE reading the thread. Renders
                  nothing when the order looks healthy or when there are no
                  orders at all. */}
              <OrderProblemBanner conversationId={conv.id} />

              {/* Polish #11 — CliffsNotes briefing sits between the frozen
                  work-head and the thread. Renders nothing when no brief is
                  cached yet and Haiku's first generation call fails. */}
              <BriefingBlock conversationId={conv.id} />

              <ThreadView
                messages={data.messages}
                tones={tones}
                attachmentsByMessageId={attachmentsByMessageId}
                onMarkProof={handleMarkProof}
                proofAttachmentIds={proofAttachmentIds}
                markProofPending={markProof.isPending}
              />

              {/* Sticky reply composer — lives at the bottom of the work
                  column so Kimi always has a place to type. Autosaves to
                  localStorage per-thread and queues Send behind a 10s undo
                  toast. Supersedes the tiny composer that used to hide in
                  the right rail. */}
              <Composer
                ref={composerRef}
                conversationId={conv.id}
                brand={conv.brand}
                onSent={advance}
                autosaveKey={conv.id}
                undoSendSeconds={10}
                templateContext={templateContext}
              />
            </div>

            {/* Right rail — Haiku's proposed actions stacked above the
                always-visible team ping panel (Polish #10). Both live inside
                a single sticky column so they scroll together and the S
                shortcut always has a valid target.
                Polish #18 — ProofTrackerPanel renders below when this thread
                has at least one proof attached; otherwise a compact empty
                state. Lives beneath TeamPingPanel so the daily driver
                (Actions + pinging Richard) stays above the fold. */}
            <div className="cc-right-rail">
              <ActionsPanel
                conversationId={conv.id}
                brand={conv.brand}
                channel={conv.channel}
                onCompleted={advance}
                onRequestCompose={focusComposer}
                onSeedCompose={seedComposer}
              />
              <TeamPingPanel
                ref={teamPingRef}
                conversationId={conv.id}
                customerName={customerName}
                orderHint={orderHint}
                channelLabel={channelLabel}
              />
              <ProofTrackerPanel conversationId={conv.id} />
            </div>
          </div>
        </div>
      )}

      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// ThreadStatusRow
// ---------------------------------------------------------------------------
// Sits directly under the work-head subject. Renders the thread-health chip
// (orange/yellow/green dot + state copy) on the left, then a compact meta
// summary on the right: "N messages · started Hh:mm am · last reply Hh:mm am".
//
// Health is allowed to be null while the conversation hook is still loading;
// in that case we render only the meta line.

type ThreadStatusRowProps = {
  health: ReturnType<typeof computeThreadHealth> | null;
  messageCount: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
};

function formatClock(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function ThreadStatusRow(props: ThreadStatusRowProps) {
  const { health, messageCount, firstMessageAt, lastMessageAt } = props;

  // Build the long-form health label that pairs the state with a hint about
  // what action keeps Kimi on track. Mirrors the mockup copy.
  const healthCopy =
    health?.state === 'urgent'
      ? 'URGENT — REPLY NOW TO KEEP THIS THREAD ON TRACK'
      : health?.state === 'aging'
        ? 'AGING — REPLY WITHIN 2H TO KEEP PRODUCTION ON TRACK'
        : health?.state === 'ontrack'
          ? 'ON TRACK'
          : null;

  const start = formatClock(firstMessageAt);
  const last = formatClock(lastMessageAt);
  const metaParts: string[] = [];
  metaParts.push(`${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`);
  if (start) metaParts.push(`started ${start}`);
  if (last && last !== start) metaParts.push(`last reply ${last}`);

  return (
    <div className="thread-status-row">
      {healthCopy ? (
        <span className={`thread-health thread-health--${health?.state ?? 'ontrack'}`}>
          <span className="dot" aria-hidden />
          {healthCopy}
        </span>
      ) : null}
      <span className="thread-meta">{metaParts.join(' · ')}</span>
    </div>
  );
}
